import type { AccessPreview, DateValue, Item, ItemRecord, LabelSelection, SurfaceSession } from './types';
import type { ItemLocalPresentation } from '../public';
export const CREATE_KINDS = ['task', 'workstream', 'board', 'event', 'person', 'document', 'project'] as const;
export const ITEM_KINDS = [...CREATE_KINDS, 'progress_entry', 'handoff'] as const;
export const STATES: Record<string, readonly string[]> = {
    task: ['open', 'active', 'waiting', 'shelved', 'done', 'cancelled'],
    workstream: ['open', 'active', 'waiting', 'shelved', 'done', 'cancelled'],
    project: ['open', 'active', 'waiting', 'shelved', 'done', 'cancelled'],
    event: ['scheduled', 'completed', 'missed', 'cancelled'],
};
export function isNumberString(value: unknown): value is string {
    return typeof value === 'string' && /^[1-9][0-9]{4,18}$/.test(value) && BigInt(value) <= 9223372036854775807n;
}
export function revision(value: unknown): value is number {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1;
}
export function record(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        throw new Error('unsupported_response');
    return value as Record<string, unknown>;
}
function decodeDate(value: unknown): Item['due'] {
    if (value === null || value === undefined)
        return null;
    if (value && typeof value === 'object') {
        const v = value as Record<string, unknown>;
        if (typeof v.time_zone === 'string') {
            try {
                new Intl.DateTimeFormat('en', { timeZone: v.time_zone });
                if (v.kind === 'date' && typeof v.date === 'string' && civilDate(v.date))
                    return v as unknown as DateValue;
                if (v.kind === 'instant' && typeof v.at === 'string' && Number.isFinite(Date.parse(v.at)))
                    return v as unknown as DateValue;
            }
            catch { /* Preserve the original unsupported representation. */ }
        }
    }
    return { kind: 'unsupported', raw: value };
}
export function decodeItem(value: unknown): ItemRecord {
    const data = record(value);
    if (typeof data.id !== 'string' || !isNumberString(data.number) || !revision(data.revision) || typeof data.kind !== 'string' || typeof data.title !== 'string')
        throw new Error('unsupported_response');
    const result: Record<string, unknown> & {
        allowed_actions: string[];
    } = { ...data, allowed_actions: Array.isArray(data.allowed_actions) ? data.allowed_actions.filter((v): v is string => typeof v === 'string') : [] };
    // A tombstone must never retain an earlier readable body's supplemental fields.
    if (data.deleted_at || data.purged_at || data.void_reason || data.source_deleted || typeof data.body !== 'string') {
        const { id, number, revision: rev, title, brief, kind, visibility, deleted_at, purged_at, void_reason, source_deleted } = result;
        return { id, number, revision: rev, title, brief, kind, visibility, deleted_at, purged_at, void_reason, source_deleted, allowed_actions: result.allowed_actions } as ItemRecord;
    }
    if (!data.kind_data || typeof data.kind_data !== 'object' || Array.isArray(data.kind_data) || !Array.isArray(data.manual_tags) || !Array.isArray(data.derived_tags) || !Array.isArray(data.labels))
        throw new Error('unsupported_response');
    return { ...result, due: decodeDate(data.due), follow_up: decodeDate(data.follow_up) } as unknown as Item;
}
export function hasBody(item: ItemRecord): item is Item { return typeof item.body === 'string' && !item.deleted_at && !item.purged_at && !('void_reason' in item && item.void_reason); }
export function editableItem(item: ItemRecord): item is Item {
    if (!hasBody(item) || !ITEM_KINDS.includes(item.kind as typeof ITEM_KINDS[number]) || !['normal', 'high', 'low'].includes(item.priority))
        return false;
    if (item.due?.kind === 'unsupported' || item.follow_up?.kind === 'unsupported')
        return false;
    const kd = item.kind_data;
    if (!kd || typeof kd !== 'object' || Array.isArray(kd))
        return false;
    if (item.kind === 'board' && (!Number.isInteger(kd.ord) || Number(kd.ord) < 0 || Number(kd.ord) > 2147483647))
        return false;
    if (item.kind === 'person' && ['account_id', 'role_text', 'organization_text'].some(key => kd[key] !== null && typeof kd[key] !== 'string'))
        return false;
    if (item.kind === 'project' && (typeof kd.name !== 'string' || typeof kd.time_zone !== 'string' || Object.keys(kd).some(key => !['name', 'time_zone', 'project_id', 'kind', 'version'].includes(key))))
        return false;
    if (item.kind === 'document' && kd.file !== null && (!kd.file || typeof kd.file !== 'object' || typeof (kd.file as Record<string, unknown>).project_id !== 'string' || typeof (kd.file as Record<string, unknown>).path !== 'string'))
        return false;
    return !STATES[item.kind] || STATES[item.kind]!.includes(item.state);
}
export function can(item: ItemRecord, ...actions: string[]): boolean { return actions.some(a => item.allowed_actions.includes(a)); }
export function doneState(state: string): boolean { return ['done', 'completed', 'cancelled', 'missed', 'closed', 'accepted', 'declined', 'dropped', 'acknowledged'].includes(state); }
export function dueIsOverdue(value:Item['due'],now:number):boolean{
    if(!value||value.kind==='unsupported')return false;
    if(value.kind==='instant')return Date.parse(value.at)<now;
    try{
        const parts=new Intl.DateTimeFormat('en-CA',{timeZone:value.time_zone,year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(new Date(now));
        const part=(name:string)=>parts.find(entry=>entry.type===name)?.value??'';
        return `${part('year')}-${part('month')}-${part('day')}`>value.date;
    }catch{return false;}
}
export function filterBoardItems<T extends {
    id: string;
    state: string;
}>(rows: readonly T[], boards: readonly {
    id: string;
    itemIds: readonly string[];
}[] | undefined, choice: ItemLocalPresentation): T[] {
    const complete = boards !== undefined;
    const assigned = new Set(boards?.flatMap(b => [...b.itemIds]));
    const selected = new Set(boards?.filter(b => choice.boardIds?.includes(b.id)).flatMap(b => [...b.itemIds]));
    return rows.filter(row => {
        const board = !complete || choice.boardIds === null || selected.has(row.id) || choice.includeNoBoard && !assigned.has(row.id);
        const status = choice.status === 'all' || choice.status === 'open' && !doneState(row.state) || choice.status === 'done' && doneState(row.state) || typeof choice.status === 'object' && row.state === choice.status.state;
        return board && status;
    });
}
export function nativePath(input: string, read = false): string {
    if (typeof input !== 'string') throw new Error('invalid_path');
    const path = input.normalize('NFC');
    if (!path)
        return '';
    const parts = path.split('/'), bytes = (value: string) => new TextEncoder().encode(value).length;
    if (bytes(path) > (read ? 1100 : 1024) || parts.length > (read ? 34 : 32) || path.startsWith('/') || /[\\\p{Cc}\p{Cs}]/u.test(path) || parts.some(p => !p || p.startsWith('.') || /[ .]$/u.test(p) || bytes(p) > 255)
        || parts.length > 1 && ['PROJECT.md', 'GLOSSARY.md', 'CONTEXT.md', 'CLAUDE.md', 'AGENTS.md'].includes(parts[0]!))
        throw new Error('invalid_path');
    return path;
}
export function tags(value: string): string[] {
    const result = [...new Set(value.split(',').map(v => v.normalize('NFKC').trim().toLowerCase()).filter(Boolean))];
    if (result.length > 64 || result.some(v => [...v].length > 64))
        throw new Error('invalid_tags');
    return result;
}
export interface BatchOperation {
    op_id: string;
    method: 'PATCH' | 'PUT' | 'POST' | 'DELETE';
    path: string;
    body: Record<string, unknown>;
}
export function editOperations(id: string, base: number, patch: Record<string, unknown>, manualTags?: string[], labels: LabelSelection[] = []): BatchOperation[] {
    let next = base;
    const operations: BatchOperation[] = [];
    if (Object.keys(patch).length)
        operations.push({ op_id: 'edit', method: 'PATCH', path: `/v1/items/${id}`, body: { ...patch, expected_revision: next++ } });
    if (manualTags)
        operations.push({ op_id: 'tags', method: 'PUT', path: `/v1/items/${id}/tags`, body: { manual_tags: manualTags, expected_revision: next++ } });
    for (const selection of labels)
        operations.push({ op_id: `label${operations.length}`, method: 'PUT', path: `/v1/items/${id}/labels/${selection.facet_id}`, body: { value_ids: selection.value_ids, expected_revision: next++ } });
    return operations;
}
export function fileCondition(mode: string, entry: {
    etag?: string | null;
    version?: number | null;
    sha256?: string | null;
} | undefined, create: boolean): {
    headers: Record<string, string>;
    body: Record<string, unknown>;
} {
    if (mode !== 'folder') throw new Error('unsupported_files_mode');
    if (create) return { headers: { 'If-None-Match': '*' }, body: {} };
    if (!entry?.etag || /[\r\n]/.test(entry.etag)) throw new Error('validator_unavailable');
    return { headers: { 'If-Match': /^(?:sha256|dir)-[0-9a-f]{64}$/.test(entry.etag) ? `"${entry.etag}"` : entry.etag }, body: {} };
}
export function parsePublicOrigin(value: unknown): string | undefined {
    if (typeof value !== 'string' || !/^https?:\/\/[^/?#@\\\s]+\/?$/i.test(value)) return;
    try { return new URL(value).origin; }
    catch { return; }
}
export function validateSurfaceSession(session: SurfaceSession, origin: string, now = Date.now(), publicOrigin?: unknown): boolean {
    try {
        const url = new URL(session.url, origin);
        if ((url.origin !== origin && url.origin !== parsePublicOrigin(publicOrigin)) || url.username || url.password || url.search || url.hash || !/^\/v1\/dashboards\/[A-Za-z0-9_-]{43}\/frame\/$/.test(url.pathname))
            return false;
        if (!Number.isFinite(Date.parse(session.expires_at)) || Date.parse(session.expires_at) <= now)
            return false;
        if (session.content_version !== null && !/^[a-f0-9]{64}$/.test(session.content_version))
            return false;
        if (session.presenter_url) {
            const presenter = new URL(session.presenter_url, origin);
            if (presenter.origin !== url.origin || presenter.pathname !== `${url.pathname}presenter.html` || presenter.username || presenter.password || presenter.hash)
                return false;
            if ([...presenter.searchParams.keys()].some(k => k !== 'session') || presenter.searchParams.size !== 1 || !/^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/.test(presenter.searchParams.get('session') ?? ''))
                return false;
        }
        return true;
    }
    catch {
        return false;
    }
}
export function previewValid(preview: AccessPreview, fingerprint: string, current: string, now = Date.now()): boolean {
    return fingerprint === current && Date.parse(preview.expires_at) > now && typeof preview.access_preview_token === 'string';
}
export function stableJson(value: unknown): string {
    if (Array.isArray(value))
        return `[${value.map(stableJson).join(',')}]`;
    if (value && typeof value === 'object')
        return `{${Object.entries(value).filter(([, v]) => v !== undefined).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(',')}}`;
    return JSON.stringify(value) ?? 'null';
}
export function civilDate(value: string): boolean { return /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(`${value}T12:00:00Z`)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value; }
